/**
 * Produces and maintains actions for controlling a DocumentContainer.  Among these are actions for:
 *
 * <ul>
 *     <li>Splitting/unsplitting the container</li>
 *     <li>Positioning the tabs within the container</li>
 * </ul>
 *
 * @module MW/layout/DocumentContainerActions
 * @copyright Copyright 2014-2015 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "MW/layout/DocumentContainer",
    "MW/layout/LayoutContainerEvent",
    "dojo/i18n!l10n/nls/layout"
], function (declare, DocumentContainer, LayoutContainerEvent, layoutL10n) {

    var LayoutAction = declare(null, {

        constructor: function (options) {
            declare.safeMixin(this, options);
            this.enabled = false;
            this.selected = false;
            this.text = layoutL10n[options.id.replace("layout.", "") + "Label"];
        }
    });

    var NO_SPLIT         = "layout.noSplit";
    var LEFT_RIGHT_SPLIT = "layout.leftRightSplit";
    var TOP_BOTTOM_SPLIT = "layout.topBottomSplit";

    var TABS_TO_TOP      = "layout.tabsToTop";
    var TABS_TO_BOTTOM   = "layout.tabsToBottom";
    var TABS_TO_LEFT     = "layout.tabsToLeft";
    var TABS_TO_RIGHT    = "layout.tabsToRight";

    var splitActionIds = [NO_SPLIT, LEFT_RIGHT_SPLIT, TOP_BOTTOM_SPLIT];
    var positionActionIds = [TABS_TO_TOP, TABS_TO_BOTTOM, TABS_TO_LEFT, TABS_TO_RIGHT];

    /**
     * @class MW/layout/DocumentContainerActions
     * @classdesc Defines a class that injects document container management actions into an action service and maintains
     * their selection and enabled states.
     */
    var DocumentContainerActions = declare(null, {

        /**
         * @param actionService - The action service into which the actions will be injected
         * @instance
         */
        constructor: function (actionService) {
            var that = this;
            var i;
            this.actionService = actionService;
            this.actions = [];

            // Create split actions
            var arrangements = [DocumentContainer.NO_SPLIT, DocumentContainer.LEFT_RIGHT_SPLIT, DocumentContainer.TOP_BOTTOM_SPLIT];
            for (i = 0; i < splitActionIds.length; i = i + 1) {
                this.actions.push(this._createSplitAction(splitActionIds[i], arrangements[i]));
            }
            // Create tab position actions
            var positions = ["top", "bottom", "left", "right"];
            for (i = 0; i < positionActionIds.length; i = i + 1) {
                this.actions.push(this._createTabPositionAction(positionActionIds[i], positions[i]));
            }

            // Load the actions
            this.actionService.loadActions(this.actions);

            // Arrange for action callbacks to fire in response to action selection changes
            this.actions.forEach(function (action) {
                that.actionService.addActionChangeCallback(action.id, function (event) {
                    if (event.property === "selected") {
                        if (event.newValue && !that._respondingToWatch) {
                            that.actionService.executeAction(action.id);
                        } else {
                            action.updateSelection();
                        }
                    }
                });
            });
        },

        /**
         * Wired up the actions to a specified DocumentContainer
         *
         * @param container {DocumentContainer} the document container to be controlled by the actions.  Can be null
         *        when no container is present in the UI.
         * @instance
         */
        setContainer: function (container) {
            var that = this;
            if (container) {
                // Attach listeners and enable/disable actions
                this._documentContainer = container;
                container.on(LayoutContainerEvent.CHILD_ADDED, function () {
                    that._updateSplitActionEnables();
                });
                container.on(LayoutContainerEvent.CHILD_REMOVED, function () {
                    that._updateSplitActionEnables();
                });
                container.watch(DocumentContainer.ARRANGEMENT, function () {
                    that._respondingToWatch = true;
                    that._updateSplitActionSelections();
                    that._respondingToWatch = false;
                });
                container.watch(DocumentContainer.TAB_POSITION, function () {
                    that._respondingToWatch = true;
                    that._updatePositionActionSelections();
                    that._respondingToWatch = false;
                });
            } else if (this._documentContainer) {
                that._documentContainer = null;
            }
            this._updateSplitActionSelections();
            this._updateSplitActionEnables();
            this._updatePositionActionEnables();
            this._updatePositionActionSelections();
        },

        _createSplitAction: function (id, arrangement) {
            var that = this;
            return new LayoutAction({
                id: id,
                callback: function () {
                    if (that._documentContainer) {
                        that._documentContainer.set(DocumentContainer.ARRANGEMENT, arrangement);
                    }
                },
                updateSelection: function () {
                    var select = false;
                    if (that._documentContainer) {
                        select = that._documentContainer.get(DocumentContainer.ARRANGEMENT) === arrangement;
                    }
                    that.actionService.updateAction(this.id, {selected: select});
                }
            });
        },

        _createTabPositionAction: function (id, position) {
            var that = this;
            var action = new LayoutAction({
                id: id,
                callback: function () {
                    if (that._documentContainer) {
                        that._documentContainer.set(DocumentContainer.TAB_POSITION, position);
                        action.updateSelection();  // Needed when current position re-selected
                    }
                },
                updateSelection: function () {
                    if (that._documentContainer) {
                        that.actionService.updateAction(this.id, {selected: that._documentContainer.get(DocumentContainer.TAB_POSITION) === position});
                    }
                }
            });
            return action;
        },

        _updateSplitActionEnables: function () {
            var moreThanOne = {enabled: !!this._documentContainer && this._documentContainer.getDocumentCount() >= 2};
            this.actionService.updateAction(NO_SPLIT, {enabled: !!this._documentContainer});
            this.actionService.updateAction(LEFT_RIGHT_SPLIT, moreThanOne);
            this.actionService.updateAction(TOP_BOTTOM_SPLIT, moreThanOne);
        },

        _updateSplitActionSelections: function () {
            var i;
            for (i = 0; i < 3; i = i + 1) {
                this.actions[i].updateSelection();
            }
        },

        _updatePositionActionEnables: function () {
            var that = this;
            var hasContainer = {enabled: !!this._documentContainer};
            positionActionIds.forEach(function (id) {
                that.actionService.updateAction(id, hasContainer);
            });
        },

        _updatePositionActionSelections: function () {
            var i;  // Position actions start at index 3 in actions array
            for (i = 3; i < this.actions.length; i = i + 1) {
                this.actions[i].updateSelection();
            }
        }
    });

    /**
     * Static action ID constants used to refer to the actions in the action service
     */
    DocumentContainerActions.NO_SPLIT         = NO_SPLIT;  /** @lends DocumentContainerActions */
    DocumentContainerActions.LEFT_RIGHT_SPLIT = LEFT_RIGHT_SPLIT;
    DocumentContainerActions.TOP_BOTTOM_SPLIT = TOP_BOTTOM_SPLIT;

    DocumentContainerActions.TABS_TO_TOP      = TABS_TO_TOP;
    DocumentContainerActions.TABS_TO_BOTTOM   = TABS_TO_BOTTOM;
    DocumentContainerActions.TABS_TO_LEFT     = TABS_TO_LEFT;
    DocumentContainerActions.TABS_TO_RIGHT    = TABS_TO_RIGHT;

    return DocumentContainerActions;
});
